import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { AlertCircle } from "lucide-react";
import { createContext, useContext, type ReactElement, Children, cloneElement, isValidElement } from "react";

interface FormFieldProps {
  label: string;
  name: string;
  error?: string;
  errors?: string[];
  required?: boolean;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * Контекст для передачи состояния ошибки в дочерние компоненты
 */
const FormFieldContext = createContext<{ hasError: boolean }>({ hasError: false });

/**
 * Хук для получения состояния ошибки в дочерних компонентах
 */
export function useFormFieldError() {
  return useContext(FormFieldContext);
}

/**
 * Form field wrapper with label, error and hint
 *
 * Поддерживает:
 * - Одну ошибку через `error`
 * - Множественные ошибки через `errors`
 * - Автоматическое добавление класса `border-destructive` к Input/Textarea
 *
 * @example
 * ```tsx
 * <FormField label="Email" name="email" error={getFieldError('email')}>
 *   <Input value={email} onChange={...} />
 * </FormField>
 * ```
 */
export function FormField({
  label,
  name,
  error,
  errors,
  required,
  hint,
  className,
  children,
}: FormFieldProps) {
  // Объединяем error и errors
  const allErrors = errors ?? (error ? [error] : []);
  const hasError = allErrors.length > 0;

  // Добавляем класс border-destructive к Input/Textarea/Select
  const enhancedChildren = Children.map(children, (child) => {
    if (isValidElement(child)) {
      const childElement = child as ReactElement<{ className?: string }>;
      const existingClassName = childElement.props.className ?? '';

      // Проверяем, что это не уже стилизованный компонент
      if (hasError && !existingClassName.includes('border-destructive')) {
        return cloneElement(childElement, {
          className: cn(existingClassName, 'border-destructive focus-visible:ring-destructive'),
        });
      }
    }
    return child;
  });

  return (
    <FormFieldContext.Provider value={{ hasError }}>
      <div className={cn("space-y-2", className)}>
        <Label htmlFor={name} className={cn("flex items-center gap-1", hasError && "text-destructive")}>
          {label}
          {required && <span className="text-destructive">*</span>}
        </Label>
        {enhancedChildren}
        {hasError && (
          <div className="space-y-1">
            {allErrors.map((err, index) => (
              <p key={index} className="text-sm text-destructive flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                {err}
              </p>
            ))}
          </div>
        )}
        {hint && !hasError && (
          <p className="text-sm text-muted-foreground">{hint}</p>
        )}
      </div>
    </FormFieldContext.Provider>
  );
}

interface FormActionsProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Form actions container (buttons)
 */
export function FormActions({ children, className }: FormActionsProps) {
  return (
    <div className={cn("flex items-center gap-3 pt-4", className)}>
      {children}
    </div>
  );
}

interface FormSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * Form section with title and description
 */
export function FormSection({
  title,
  description,
  children,
  className,
}: FormSectionProps) {
  return (
    <div className={cn("space-y-4", className)}>
      <div>
        <h3 className="text-lg font-medium">{title}</h3>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
